import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { formatError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

export default function Login() {
    const { login } = useAuth();
    const { t } = useLanguage();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const u = await login(email, password);
            toast.success(t("login.welcomeBack", { name: u.name }));
            navigate(u.role === "admin" ? "/admin" : "/dashboard", { replace: true });
        } catch (err) {
            toast.error(formatError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-8 relative">
            <div className="absolute top-6 right-6"><LanguageSwitcher /></div>
            <div className="flex items-center justify-center w-full">
                <form onSubmit={onSubmit} className="w-full max-w-md space-y-8" data-testid="login-form">
                    <Link to="/" className="flex items-center gap-1" data-testid="login-brand">
                        <img src="https://customer-assets.emergentagent.com/job_schedule-sync-109/artifacts/0i7pk9q1_refpairlogo_trans.png" alt="RefPair" className="w-14 h-14 object-contain" />
                        <span className="font-heading text-xl font-semibold tracking-tight">RefPair<span className="text-muted-foreground font-normal text-[0.72em] tracking-tighter -ml-[1px]">.com</span></span>
                    </Link>
                    <div>
                        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">{t("login.title")}</h1>
                        <p className="text-muted-foreground mt-2">{t("login.subtitle")}</p>
                    </div>

                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">{t("common.email")}</Label>
                            <Input id="email" type="email" required value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="h-12 rounded-md" data-testid="login-email-input" />
                        </div>
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <Label htmlFor="password">{t("common.password")}</Label>
                                <Link to="/forgot-password" className="text-sm text-primary hover:underline" data-testid="login-forgot-link">
                                    {t("login.forgot")}
                                </Link>
                            </div>
                            <Input id="password" type="password" required value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="h-12 rounded-md" data-testid="login-password-input" />
                        </div>
                    </div>

                    <Button type="submit" disabled={loading}
                        className="w-full h-12 rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
                        data-testid="login-submit-button">
                        {loading ? t("common.signingIn") : t("common.signIn")}
                    </Button>

                    <div className="text-sm text-muted-foreground text-center">
                        {t("login.noAccount")}{" "}
                        <Link to="/register" className="text-primary hover:underline" data-testid="login-register-link">
                            {t("login.createOne")}
                        </Link>
                    </div>
                </form>
            </div>
        </div>
    );
}
